import {
  ContextualMenuItemType,
  DirectionalHint,
  IButtonProps,
  IconButton,
  Shimmer,
  Stack,
  TooltipHost
} from "@fluentui/react";
import { getNextColor, IChartProps, IVerticalBarChartDataPoint, VerticalBarChart } from "@fluentui/react-charting";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomHorizontalBarChart from "../BasicComponents/CustomHorizontalBarChart";
import FluentDonutChart from "../ChartComponents/FluentDountChart";
import { LineChartComponent } from "../ChartComponents/LineChartFluent";
import { CUSTOMER_VIEW_STYLES } from "../components/CustomerView/CustomerView.styles";
import { CustomShimmer } from "../helper/CustomShimmer";
import { selectViewport } from "../slices/UI.slice";
import { useAppSelector } from "../store/hooks";
import {
  calculateAverages,
  getSumForNumberValues,
  groupBy,
  groupByAverage,
  splitColumnValues
} from "../Utils/js-common";
import CustomCard from "./CustomCard";
import {
  ChartOption,
  CustomCardWithMetricItem,
  CustomCardWithMetricsProps
} from "./Interfaces/CustomCardWithMetrics.interfaces";
import {
  classNames,
  contextualMenuStyles,
  customCardStyles,
  horizontalBarHeaderShimmerStyles,
  iconButtonStyles
} from "./Styles/CustomCardWithMetrics.styles";

export const CustomCardWithMetrics = ({
  loading,
  metricFilters,
  onDataPointClick,
  onResetFilters,
  options,
  enableUrlParams = false
}: CustomCardWithMetricsProps<any>) => {
  // Properties
  const customCardContainerRef = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const disableCustomCards = loading === false && !options?.originalData?.length;
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitializedRef = useRef(false);

  const [startDate, setStartDate] = useState(() => {
    // Try to get the latest date from options.filteredData
    if (options && Array.isArray(options.filteredData) && options.filteredData.length > 0) {
      // Find all date values in the data
      // Use the field passed as the date key
      const field = options.chartOptions && options.chartOptions[0] && options.chartOptions[0].field;
      const dateValues = options.filteredData
        .map((item) => {
          if (
            field &&
            item[field] &&
            (item[field] instanceof Date || (typeof item[field] === "string" && !isNaN(Date.parse(item[field]))))
          ) {
            return new Date(item[field]);
          }
          return null;
        })
        .filter((d): d is Date => d !== null && !isNaN(d.getTime()));
      if (dateValues.length > 0) {
        // Get the latest date
        const timestamps = dateValues.map((d) => d.getTime());
        const maxTimestamp = timestamps.length > 0 ? timestamps.reduce((max, val) => (val > max ? val : max), -Infinity) : Date.now();
        const latestDate = new Date(maxTimestamp);
        // Set startDate to 11 months before the latest date
        latestDate.setMonth(latestDate.getMonth() - 11);
        return latestDate;
      }
    }
    // Fallback to 11 months before today
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    return date;
  });
  const [previousMonthYearData, setPreviousMonthYearData] = useState<Record<string, any[]>>({});

  // Function to parse metric filters from URL (now returns array)
  const getMetricFiltersFromUrl = useCallback((): ChartOption[] => {
    if (!enableUrlParams) return [];

    const metricFilterParam = searchParams.get("mf");
    if (!metricFilterParam) return [];

    try {
      const decodedFilter = atob(metricFilterParam);
      const parsed = JSON.parse(decodedFilter);
      // Handle both single filter (legacy) and array of filters
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.warn("Failed to parse URL metric filter parameters:", error);
      return [];
    }
  }, [searchParams, enableUrlParams]);

  // URL parameter update function (now handles multiple filters)
  const updateMetricFilterUrl = useCallback(
    (filterData: ChartOption, shouldReplace: boolean = false) => {
      if (!enableUrlParams) return;

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);

        if (filterData && Object.keys(filterData).length > 0) {
          // Get existing filters
          const existingFilters = shouldReplace ? [] : getMetricFiltersFromUrl();

          // Create a serializable filter object
          const metricFilterData = {
            field: filterData.field,
            value: filterData.value,
            title: filterData.title,
            type: filterData.type
          };

          // Check if this filter already exists (to avoid duplicates)
          const filterExists = existingFilters.some(
            (filter) => filter.field === metricFilterData.field && filter.value === metricFilterData.value
          );

          let updatedFilters;
          if (filterExists) {
            // If filter exists, remove it (toggle behavior)
            updatedFilters = existingFilters.filter(
              (filter) => !(filter.field === metricFilterData.field && filter.value === metricFilterData.value)
            );
          } else {
            // Add new filter to existing ones
            updatedFilters = [...existingFilters, metricFilterData];
          }

          if (updatedFilters.length > 0) {
            // Convert array to JSON string first, then encode with btoa
            const jsonString = JSON.stringify(updatedFilters);
            const encodedValue = btoa(jsonString);
            newParams.set("mf", encodedValue);
          } else {
            newParams.delete("mf");
          }
        } else {
          newParams.delete("mf");
        }

        return newParams;
      });
    },
    [setSearchParams, getMetricFiltersFromUrl, enableUrlParams]
  );

  // Enhanced onDataPointClick wrapper
  const handleDataPointClick = useCallback(
    (filterData: ChartOption) => {
      // Update URL parameters only if enabled
      if (enableUrlParams) {
        updateMetricFilterUrl(filterData);
      }

      // Call the original onDataPointClick prop
      onDataPointClick(filterData);
    },
    [onDataPointClick, updateMetricFilterUrl, enableUrlParams]
  );

  // Initialize metric filters from URL params on component mount
  useEffect(() => {
    if (loading || isInitializedRef.current || !enableUrlParams) return;

    const urlMetricFilter = getMetricFiltersFromUrl();
    if (urlMetricFilter && urlMetricFilter.length > 0) {
      try {
        // Apply each filter from URL
        for (const filter of urlMetricFilter) {
          onDataPointClick(filter);
        }
      } catch (error) {
        console.warn("Failed to apply URL metric filter:", error);
        // Clean up invalid metric filter params from URL
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("mf");
          return newParams;
        });
      }
    }

    isInitializedRef.current = true;
  }, [loading, getMetricFiltersFromUrl, onDataPointClick, setSearchParams, enableUrlParams]);

  const handleNextClick = () => {
    setStartDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 12);
      return newDate;
    });
  };

  const handlePreviousClick = () => {
    setStartDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 12);
      return newDate;
    });
  };

  // Events
  const getGroupedData = (key: string, data: any[]) => {
    const arr = Array.isArray(data) ? data : [];
    const filteredData = arr.filter((item) => item && item[key]);
    const groupedData = groupBy(filteredData, key);

    return Object.keys(groupedData)?.map((item) => ({
      name: item,
      value: groupedData[item].length
    }));
  };
  const handleResize = useCallback((width: number) => setWidth(width), []);
  const getHighlightedStatus = () => Object.keys(metricFilters).length;
  const horizontalBarHeader = (title: string | undefined, isLoading: boolean) => {
    const iconButtonProps: IButtonProps = {
      ariaLabel: "Applied Filters",
      menuProps: {
        directionalHint: DirectionalHint.bottomRightEdge,
        items: [
          {
            key: "selectedFiltersHeader",
            itemType: ContextualMenuItemType.Header,
            text: "Selected Filters",
            itemProps: {
              styles: {
                label: `${classNames.layoutText} ${classNames.headerLabel}`
              }
            }
          },
          {
            key: "selectedFiltersContext",
            onRender: (): ReactNode => {
              if (!Object.keys(metricFilters).length) {
                return <></>;
              }
              const formatValue = (value: string) => {
                const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
                if (value && dateRegex.test(value) && !isNaN(new Date(value).getTime())) {
                  return new Date(value).toISOString().substring(0, 10);
                }
                return value;
              };
              return (
                <div className={classNames.appliedFiltersContainer}>
                  {Object.keys(metricFilters)?.map((key) => (
                    <>
                      <div key={1} className={`${classNames.layoutText} ${classNames.nameText}`}>
                        {key}
                      </div>
                      <div className={classNames.essentialsSeparator}>:</div>
                      <div key={2} className={`${classNames.layoutText} ${classNames.secondaryText}`}>
                        {" "}
                        {formatValue(metricFilters[key].toString()) || "(empty)"}
                      </div>
                    </>
                  ))}
                </div>
              );
            }
          }
        ],
        styles: contextualMenuStyles
      },
      title: "Applied Filters"
    };

    if (isLoading) {
      return <Shimmer styles={horizontalBarHeaderShimmerStyles} />;
    }

    return (
      <Stack horizontal styles={{ root: { alignItems: "center", justifyContent: "space-between" } }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{title}</span>
          {options?.chartOptions.find((x: any) => x.tooltipDescription?.length > 0) && (
            <TooltipHost
              content={
                options.chartOptions.find((x: any) => x.tooltipDescription?.length > 0 && x.title == title)
                  ?.tooltipDescription
              }
              id="tooltip"
            >
              <IconButton
                iconProps={{ iconName: "Info" }}
                styles={{
                  root: {
                    marginTop: 5
                  }
                }}
              />
            </TooltipHost>
          )}
        </div>
        {getHighlightedStatus() ? (
          <Stack horizontal>
            <TooltipHost content="Clear all filters by clicking here." calloutProps={{ gapSpace: 0 }}>
              <IconButton
                iconProps={{ iconName: "ClearFilter" }}
                ariaLabel="Clear All Filters"
                onClick={() => {
                  // Clear URL parameters when resetting filters (only if enabled)
                  if (enableUrlParams) {
                    setSearchParams((prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.delete("mf");
                      return newParams;
                    });
                  }
                  onResetFilters();
                }}
              />
            </TooltipHost>
            <TooltipHost content="Filters already in use" calloutProps={{ gapSpace: 0 }}>
              <IconButton {...iconButtonProps} />
            </TooltipHost>
          </Stack>
        ) : (
          ""
        )}
      </Stack>
    );
  };
  const itemMemo: CustomCardWithMetricItem[] = useMemo(() => {
    if (disableCustomCards) return [];

    return (options || { chartOptions: [], originalData: [], filteredData: [] }).chartOptions?.map((option) => {
      const { field, key, title, type, id, legendName } = option;
      let data = options?.filteredData ?? [];

      if (option.splitField) {
        const { splitField, splitBy } = option.splitField;
        data = splitColumnValues(options?.filteredData ?? [], splitField, splitBy);
      }

      let groupedData: any[] = [];
      if (option.aggregationType) {
        const { aggregationField } = option.aggregationType;
        const filteredData = data.filter((item) => item[aggregationField]);
        const sumData = groupByAverage(filteredData, field, aggregationField);
        groupedData = calculateAverages(sumData);
      } else {
        groupedData = getGroupedData(field, data);
      }

      const yearsInData = groupedData.map((item) => new Date(item.name).getFullYear()).filter((year) => !isNaN(year));

      switch (type) {
        case "line": {
          const allDataPoints = groupedData
            ?.map(({ name, value }) => ({
              x: new Date(new Date(name).toISOString()),
              y: value,
              onDataPointClick: () => handleDataPointClick({ ...option, value: name })
            }))
            .sort((a, b) => a.x.getTime() - b.x.getTime());

          let dataByMonthYear = allDataPoints;
          let showNavigation = false;
          let sixMonthsLater: Date;

          if (allDataPoints.length > 12) {
            showNavigation = true;
            sixMonthsLater = new Date(startDate);
            sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 12);
            dataByMonthYear = sixMonthsLater
              ? allDataPoints.filter((item) => item.x >= startDate && item.x < sixMonthsLater)
              : allDataPoints;
          }

          if (dataByMonthYear.length > 0) {
            setPreviousMonthYearData((prev) => ({ ...prev, [field]: dataByMonthYear }));
          }
          const chartData: IChartProps = {
            lineChartData: [
              {
                legend: legendName,
                data: dataByMonthYear.length > 0 ? dataByMonthYear : (previousMonthYearData[field] ?? []),
                color: "#637cef",
                lineOptions: {
                  lineBorderWidth: "4"
                }
              }
            ]
          };

          const newtickValues =
            dataByMonthYear.length > 0
              ? dataByMonthYear.filter((_, index) => index % 2 === 0)?.map((point) => point.x)
              : (previousMonthYearData[field] ?? []).filter((_, index) => index % 2 === 0)?.map((point) => point.x);

          return {
            component: () => (
              <>
                <LineChartComponent
                  data={chartData}
                  allowResize={true}
                  tickValues={newtickValues}
                  height={250}
                  width={width}
                  xAxisTitle={option.xaxisTitle}
                  yAxisTitle={option.yaxisTitle}
                  isLoading={loading}
                  {...(option.yAxisTickFormat ? { yAxisTickFormat: option.yAxisTickFormat } : {})}
                />
                {!loading ? (
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "-10px", gap: "15px" }}>
                    <IconButton
                      iconProps={{ iconName: "ChevronLeft" }}
                      title="Previous"
                      onClick={handlePreviousClick}
                      disabled={
                        allDataPoints.length > 12
                          ? startDate.getFullYear() === Math.min(...yearsInData)
                          : !showNavigation
                      }
                      styles={iconButtonStyles}
                    />
                    <IconButton
                      iconProps={{ iconName: "ChevronRight" }}
                      title="Next"
                      onClick={handleNextClick}
                      disabled={
                        allDataPoints.length > 12 ? sixMonthsLater?.getTime() >= new Date().getTime() : !showNavigation
                      }
                      styles={iconButtonStyles}
                    />
                  </div>
                ) : null}
              </>
            ),
            key,
            title
          };
        }
        case "donut": {
          const sortedGroupedData = groupedData?.slice().sort((a, b) => b.value - a.value);
          const chartData = sortedGroupedData?.map((item, index) => ({
            legend: item.name,
            data: item.value,
            color: getNextColor(index),
            onClick: () => handleDataPointClick({ ...option, value: item.name })
          }));
          const valueInsideDonut = getSumForNumberValues(chartData?.map(({ data }) => data ?? 0));
          const component = () => (
            <FluentDonutChart
              data={{
                chartTitle: "Donut Chart",
                chartData: chartData
              }}
              key={chartData?.length}
              height={230}
              innerRadius={50}
              isChartLoading={loading}
              styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
              valueInsideDonut={valueInsideDonut}
              width={width ? width : 300}
            />
          );
          return {
            component,
            key,
            title
          };
        }
        case "verticalbar": {
          const points: IVerticalBarChartDataPoint[] = groupedData?.map((item, index) => ({
            x: item.name,
            y: item.value,
            color: getNextColor(index),
            onClick: () => handleDataPointClick({ ...option, value: item.name })
          }));

          const component = () =>
            loading ? (
              <CustomShimmer columnCount={4} rowCount={8} styles={{ root: { height: 300 } }} />
            ) : (
              <VerticalBarChart
                chartTitle="Vertical Bar Chart"
                data={points}
                barWidth={50} // Adjust bar width for better responsiveness
                yAxisTickCount={5}
                hideLegend={true}
                enableReflow={true}
                strokeWidth={3}
                height={150}
                styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.verticalBarChart}
                width={width}
                maxBarWidth={24}
              />
            );
          return {
            component,
            key,
            title
          };
        }
        case "horizontalbar": {
          const sortedData = groupedData?.sort((a, b) => b.value - a.value).slice(0, 10); // Limit to top 10 items

          const formattedData = sortedData?.map(({ name, value }) => {
            return {
              chartTitle: name,
              chartData: [
                {
                  legend: name || "",
                  horizontalBarChartdata: {
                    x: Number(value),
                    y: 10000 // Adjust the y value based on your logic
                  },
                  onClick: () => handleDataPointClick({ ...option, value: name }),
                  color: getNextColor(id) // Updated color
                }
              ]
            };
          });

          const component = () =>
            sortedData && (
              <div style={{ padding: "10px" }}>
                <CustomHorizontalBarChart data={formattedData} minHeight={300} itemsPerPage={5} isLoading={loading} />
              </div>
            );

          return {
            component,
            key,
            title
          };
        }
        default:
          break;
      }

      return {
        key: "",
        title: "",
        component: () => <></>
      };
    });
  }, [options, loading, width, startDate]);

  // Hookes
  useEffect(() => {
    if (customCardContainerRef.current) {
      handleResize(customCardContainerRef.current?.offsetWidth);
    }
  }, [resizedWidth, customCardContainerRef]);

  return itemMemo?.map(({ component, key, title }, index) => (
    <CustomCard
      key={key}
      cardHeaderTitle={horizontalBarHeader(title, loading)}
      styles={customCardStyles}
      kustoLink={options?.chartOptions[index]?.kustoLink}
      isLoading={loading}
      chartTitle={title}
    >
      <div ref={customCardContainerRef} className={classNames.chartContainer}>
        {component()}
      </div>
    </CustomCard>
  ));
};
